import * as signalR from "@microsoft/signalr";
import common from "../../helper/common";
import AppConstant from "../../constants/appConstant";
import SignalRHandlers from "./signalRHandlers";
import SignalRRooms from "./signalRRooms";

/**
 * SignalR Service
 * Main service for managing SignalR connection
 */
class SignalRService {
  static instance = null;
  connection = null;
  isConnected = false;
  reconnectAttempts = 0;
  maxReconnectAttempts = 5;
  handlers = null;
  pendingCallbacks = {
    clubStatus: null,
    permissionStatus: null,
    forceLogout: null
  };

  static getInstance() {
    if (!SignalRService.instance) {
      SignalRService.instance = new SignalRService();
    }
    return SignalRService.instance;
  }

  /**
   * Initialize SignalR connection
   */
  async initialize(userId) {
    if (!userId) {
      console.warn("[SignalR] Cannot initialize - no userId provided");
      return false;
    }

    // Disconnect existing connection if any
    if (this.connection) {
      try {
        await this.disconnect();
      } catch (e) {
        console.warn("[SignalR] Error disconnecting existing connection:", e);
      }
    }

    // Get base URL from config
    const baseUrl = AppConstant.baseUrl;
    // SignalR hub URL - matches backend: app.MapHub<WebSocket>("/hub")
    const hubUrl = baseUrl.endsWith("/") ? `${baseUrl}hub` : `${baseUrl}/hub`;

    let token = null;
    try {
      // Get token for authentication
      token = await common.getToken();
      
      if (!token) {
        return false;
      }

      // Create connection with proper authentication
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => token,
        })
        .withAutomaticReconnect()
        .build();

      // Initialize handlers
      this.handlers = new SignalRHandlers(this.connection);

      // Register any pending callbacks that were set before initialization
      if (this.pendingCallbacks.clubStatus) {
        this.handlers.registerClubStatusHandler(this.pendingCallbacks.clubStatus);
        this.pendingCallbacks.clubStatus = null;
      }
      if (this.pendingCallbacks.permissionStatus) {
        this.handlers.registerPermissionStatusHandler(this.pendingCallbacks.permissionStatus);
        this.pendingCallbacks.permissionStatus = null;
      }
      if (this.pendingCallbacks.forceLogout) {
        this.handlers.registerForceLogoutHandler(this.pendingCallbacks.forceLogout);
        this.pendingCallbacks.forceLogout = null;
      }

      // Register connection event handlers BEFORE starting
      this.connection.onclose((error) => {
        this.isConnected = false;
        this.reconnectAttempts++;
      });

      this.connection.onreconnecting(() => {
        this.isConnected = false;
      });

      this.connection.onreconnected(async () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Rejoin groups after reconnection
        await SignalRRooms.joinRooms(this.connection);
      });

      // Register all event handlers on connection (BEFORE starting)
      if (this.handlers) {
        this.handlers.registerAllHandlers();
      }

      // Start connection
      await this.connection.start();
      this.isConnected = true;
      this.reconnectAttempts = 0;

      // Wait a moment for connection to be fully ready, then join rooms
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Join rooms (user.id and user.defaultClubId)
      await SignalRRooms.joinRooms(this.connection);

      return true;
    } catch (error) {
      console.error("[SignalR] Connection failed:", error.message);
      this.isConnected = false;
      return false;
    }
  }

  /**
   * Register handler for ClubStatus event
   */
  onClubStatusChange(callback) {
    if (this.handlers) {
      // Handlers exist, register immediately
      this.handlers.registerClubStatusHandler(callback);
    } else {
      // Handlers don't exist yet (before initialize), store for later
      this.pendingCallbacks.clubStatus = callback;
    }
  }

  /**
   * Register handler for PermissionStatus event
   */
  onPermissionStatusChange(callback) {
    if (this.handlers) {
      // Handlers exist, register immediately
      this.handlers.registerPermissionStatusHandler(callback);
    } else {
      // Handlers don't exist yet (before initialize), store for later
      this.pendingCallbacks.permissionStatus = callback;
    }
  }

  /**
   * Register handler for ForceLogout event
   */
  onForceLogout(callback) {
      if (this.handlers) {
          // Handlers exist, register immediately
        this.handlers.registerForceLogoutHandler(callback);
      } else {
          // Handlers don't exist yet (before initialize), store for later
        this.pendingCallbacks.forceLogout = callback;
    }
}
  /**
   * Disconnect SignalR
   */
  async disconnect() {
    if (this.connection) {
      try {
        await this.connection.stop();
        this.isConnected = false;
        this.handlers = null;
      } catch (error) {
        console.error("[SignalR] Error disconnecting:", error);
      }
    }
  }

  /**
   * Check if connected
   */
  getConnectionState() {
    return this.isConnected && this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

export default SignalRService;

